/**
 * Endpoint map for the Conbent Core microservice, IdentityServer4 and Conbent.Notifications.
 *
 * Every path here is backed by Conbent Core, the separately deployed IdentityServer, or the
 * Notifications service (`notifications` only).
 * Two conventions of the Core service leak through
 * and are worth knowing before adding to this file:
 *
 * - Routes come from `[Route("api/[controller]")]`, so the segment is the controller's
 *   class name minus the suffix and keeps its casing — `api/RevitFileFamily`, not
 *   `api/revit-file-family`. `api/audit-checks` is the one controller that overrides it.
 * - Most collections are fetched with a `GetAll` or `GetById/{id}` action rather than by
 *   verb on a bare resource path, because the storage controllers are thin wrappers over
 *   gRPC services and mirror their method names.
 *
 * Where the Core service offers no route for something the client needs, there is no entry
 * here and the service layer says so; see `docs/core-api-compatibility.md`.
 */
export const API_ENDPOINTS = {
  /** Merge Model Requests (Conbent ADR 0010). Core answers 404 on every route while the feature is off. */
  mergeRequests: {
    /** Answers `{ enabled }` for any signed-in user, even while every other route below is a 404. */
    feature: '/api/v1/merge-model-requests/feature',
    list: '/api/v1/merge-model-requests',
    /** The models the caller is a member of, for the model picker. */
    models: '/api/v1/merge-model-requests/models',
    /** Current SHAR and PROD of a model plus its baseline history (planned in Core, see the baselines plan). */
    baselines: (modelId: string): string =>
      `/api/v1/merge-model-requests/models/${encodeURIComponent(modelId)}/baselines`,
    revert: (modelId: string, stage: 'shared' | 'published'): string =>
      `/api/v1/merge-model-requests/models/${encodeURIComponent(modelId)}/baselines/${stage}/revert`,
    /** People who can be named as reviewer or authorizer for a model. */
    participants: (modelId: string): string =>
      `/api/v1/merge-model-requests/models/${encodeURIComponent(modelId)}/participants`,
    diff: (id: string): string => `/api/v1/merge-model-requests/${encodeURIComponent(id)}/diff`,
    diffRetry: (id: string): string => `/api/v1/merge-model-requests/${encodeURIComponent(id)}/diff/retry`,
    finalizeRetry: (id: string): string =>
      `/api/v1/merge-model-requests/${encodeURIComponent(id)}/finalize-retry`,
    detail: (id: string): string => `/api/v1/merge-model-requests/${encodeURIComponent(id)}`,
    withdraw: (id: string): string => `/api/v1/merge-model-requests/${encodeURIComponent(id)}/withdraw`,
    comments: (id: string): string => `/api/v1/merge-model-requests/${encodeURIComponent(id)}/comments`,
    reviewDecision: (id: string): string =>
      `/api/v1/merge-model-requests/${encodeURIComponent(id)}/review-decision`,
    authorizerDecision: (id: string): string =>
      `/api/v1/merge-model-requests/${encodeURIComponent(id)}/authorizer-decision`,
    auditTrail: (id: string): string =>
      `/api/v1/merge-model-requests/${encodeURIComponent(id)}/audit-trail`,
    /** Authenticated binary stream of the archived `.rvt`; fetch it as a blob, never as a plain link. */
    archivedFile: (modelId: string, sha256: string): string =>
      `/api/v1/merge-model-requests/archive/${encodeURIComponent(modelId)}/files/${encodeURIComponent(sha256)}`,
  },

  auth: {
    login: '/api/authentication/web/login',
    refresh: '/api/authentication/web/refresh',
    logout: '/api/authentication/web/logout',
  },

  profile: {
    /** IdentityServer self-service routes; these are not hosted by Core. */
    me: '/api/users/me',
    update: '/api/users/me',
    settings: '/api/users/me/settings',
    avatar: '/api/users/me/avatar',
  },

  families: {
    /** `RevitFileFamilyController.GetAllFamilies` — takes `pageIndex`/`pageSize`/`isCount`. */
    list: '/api/RevitFileFamily/GetAll',
    /**
     * The library's search, filter, facet and pagination surface.
     *
     * A POST because the filter set is too large for a query string, and the only endpoint
     * that can page and filter the content library; `GetAll` cannot.
     */
    search: '/api/RevitProjectContentSearch/Query',
    /** Families loaded into a project, by that project's model id. */
    allProjectFamilies: '/api/RevitProjectFamily/GetAll',
    metadataRevisions: (familyId: number): string => `/api/v1/families/${String(familyId)}/metadata-revisions`,
    metadataRevision: (familyId: number, revisionId: number): string =>
      `/api/v1/families/${String(familyId)}/metadata-revisions/${String(revisionId)}`,
    instanceMetadataSelection: (modelId: number, uniqueId: string): string =>
      `/api/v1/models/${String(modelId)}/family-instances/${encodeURIComponent(uniqueId)}/metadata-selection`,
    projectFamilies: (modelId: number): string => `/api/RevitProjectFamily/GetById/${String(modelId)}`,
    /** Placed occurrences within a model, by that model's id. */
    instances: (modelId: number): string => `/api/RevitFamilyInstances/GetById/${String(modelId)}`,
    /** One authenticated binary GLB, addressed by a placed instance geometry hash. */
    instanceGlb: (hashId: string): string =>
      `/api/RevitFamilyInstances/GetGlbById/${encodeURIComponent(hashId)}`,
    /** Geometry manifest (metadata + per-element GLB URLs) for the project viewer. */
    modelGeometry: (modelId: number): string =>
      `/api/RevitFamilyInstances/GetAllWithGeometry/${String(modelId)}`,
  },

  /**
   * What this Core deployment supports. An older Core has no such route and answers 404,
   * which the client reads as "none of the v1 features", so it never shows a page the
   * deployed service cannot serve.
   */
  capabilities: '/api/v1/capabilities',

  /**
   * The Core project workspace (`/api/v1`): tenants, projects and their members.
   *
   * Unrelated to `projects` below, which serves Revit models. These routes follow the v1
   * conventions instead of the legacy ones: string enums, ISO dates with `Z`, cursor pages
   * and Problem Details with a `code`.
   */
  workspace: {
    tenants: '/api/v1/tenants',
    projects: '/api/v1/projects',
    project: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}`,
    members: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/members`,
    member: (projectId: string, memberId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
    models: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/models`,
    /** The dashboard's counts, in one request. */
    summary: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/summary`,
    /** Every linked model's health checks, in one request. */
    health: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/health`,
  },

  /** A workspace project's documents (`/api/v1`). Multi-value filters repeat the query key. */
  documents: {
    list: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/documents`,
    sheets: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/sheets`,
    facets: (projectId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/facets`,
    document: (projectId: string, documentId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}`,
    restore: (projectId: string, documentId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/restore`,
    revisions: (projectId: string, documentId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/revisions`,
    revisionStatus: (projectId: string, documentId: string, revisionId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/revisions/${encodeURIComponent(revisionId)}/status`,
    contentUrl: (projectId: string, documentId: string, revisionId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/revisions/${encodeURIComponent(revisionId)}/content-url`,
  },

  /** A project's manifest (`/api/v1`): the active one, validation and activation. */
  manifest: {
    active: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/manifest`,
    validate: (projectId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/manifest/validate`,
    activate: (projectId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/manifest/activate`,
    revisionValidation: (projectId: string, documentId: string, revisionId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/revisions/${encodeURIComponent(revisionId)}/manifest-validation`,
  },

  /** Upload sessions and the assets they produce (`/api/v1`). Part bytes go to storage, not here. */
  uploads: {
    create: (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}/uploads`,
    session: (projectId: string, uploadId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/uploads/${encodeURIComponent(uploadId)}`,
    partUrls: (projectId: string, uploadId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/uploads/${encodeURIComponent(uploadId)}/part-urls`,
    complete: (projectId: string, uploadId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/uploads/${encodeURIComponent(uploadId)}/complete`,
    asset: (projectId: string, assetId: string): string =>
      `/api/v1/projects/${encodeURIComponent(projectId)}/assets/${encodeURIComponent(assetId)}`,
  },

  projects: {
    /** Every stored Revit document. The legacy routes have no separate project entity. */
    list: '/api/RevitFileProject/GetAll',
    getById: (id: number): string => `/api/RevitFileProject/GetById/${String(id)}`,
    getByName: (name: string): string => `/api/RevitFileProject/GetByName/${encodeURIComponent(name)}`,
  },

  audits: {
    list: '/api/Audit',
    getById: (id: number): string => `/api/Audit/${String(id)}`,
    create: '/api/Audit',
    update: (id: number): string => `/api/Audit/${String(id)}`,
    remove: (id: number): string => `/api/Audit/${String(id)}`,
    archive: (id: number): string => `/api/Audit/${String(id)}/archive`,
    clone: (id: number): string => `/api/Audit/${String(id)}/clone`,
    execute: (id: number): string => `/api/Audit/${String(id)}/execute`,
    /** Runs an audit that checks the file families library; no model is involved. */
    executeLibrary: (id: number): string => `/api/Audit/${String(id)}/execute-library`,
    /** Runs of every families-library audit, newest first. */
    libraryRuns: '/api/Audit/library/instances',
    instances: (id: number): string => `/api/Audit/${String(id)}/instances`,
    instance: (instanceId: number): string => `/api/Audit/instances/${String(instanceId)}`,
    instanceResults: (instanceId: number): string => `/api/Audit/instances/${String(instanceId)}/results`,
    statistics: '/api/Audit/statistics',
    search: '/api/Audit/search',
    checks: (id: number): string => `/api/Audit/${String(id)}/checks`,
    check: (id: number, checkId: number): string => `/api/Audit/${String(id)}/checks/${String(checkId)}`,
    criteria: (id: number): string => `/api/Audit/${String(id)}/criteria`,
    checkTypes: '/api/audit-checks/types',
    export: '/api/Audit/export',
    /** Proposed routes: an ordered audit chain per model. Not served by Core yet. */
    chain: (modelId: number): string => `/api/Audit/chains/model/${String(modelId)}`,
    chainRun: (modelId: number): string => `/api/Audit/chains/model/${String(modelId)}/run`,
    /** Switch: run the chain by itself after every new snapshot (sync) of the model. */
    chainAutoRun: (modelId: number): string => `/api/Audit/chains/model/${String(modelId)}/auto-run`,
    chainRuns: (modelId: number): string => `/api/Audit/chains/model/${String(modelId)}/runs`,
  },

  snapshots: {
    list: '/api/SnapshotMetaData/GetAll',
    getById: (id: number): string => `/api/SnapshotMetaData/GetById/${String(id)}`,
    byModel: (modelId: number): string => `/api/SnapshotMetaData/GetByRevitModelId/${String(modelId)}`,
    recent: '/api/SnapshotMetaData/GetRecent',
    count: '/api/SnapshotMetaData/Count',
    /** Element and family counts for one snapshot, which the metadata row does not carry. */
    statistics: (id: number): string => `/api/SnapshotMetaData/GetStatistics/${String(id)}`,
  },

  /**
   * Element-level differences between consecutive model versions.
   *
   * The Core service computes these when a snapshot is ingested and stores the result, so
   * a comparison is fetched rather than requested; there is no endpoint that diffs two
   * arbitrary snapshots on demand.
   */
  diffs: {
    list: '/api/RevitModelDiffs',
    getById: (id: number): string => `/api/RevitModelDiffs/${String(id)}`,
    byModel: (modelId: number): string => `/api/RevitModelDiffs/ByModel/${String(modelId)}`,
    timeline: (modelId: number): string => `/api/RevitModelDiffs/TimelineByModel/${String(modelId)}`,
    details: (id: number): string => `/api/RevitModelDiffs/${String(id)}/Details`,
    summary: '/api/RevitModelDiffs/Summary',
  },

  /** Conbent.Notifications: what the signed-in user follows, and their stored address. */
  notifications: {
    watches: '/api/v1/watches',
    watch: (watchId: string): string => `/api/v1/watches/${encodeURIComponent(watchId)}`,
    /** Refreshes the stored address and verified flag from the caller's token. */
    me: '/api/v1/me',
  },

  modelEvents: {
    list: '/api/ModelEvents',
    byType: '/api/ModelEvents/ByType',
    timeRange: '/api/ModelEvents/TimeRange',
    recentActivity: '/api/ModelEvents/RecentActivity',
    aggregates: '/api/ModelEvents/Aggregates',
    currentVersion: '/api/ModelEvents/CurrentVersion',
    stateAtVersion: '/api/ModelEvents/StateAtVersion',
  },
} as const;

